'use client'

import { DIALOG_BASE } from '@trustoo/components/modal/constants/dialogPresets'
import { useAnimatedDialog } from '@trustoo/components/modal/hooks/useAnimatedDialog'
import { getDialogStatusClassName } from '@trustoo/components/modal/hooks/useDialogTransition'
import { DialogRootProps } from '@trustoo/components/modal/PlainDialog'
import { TransitionStatus } from 'react-transition-state'
import { twMerge } from 'tailwind-merge'

export interface AnimatedDialogProps extends DialogRootProps {
    /** Override the per-status animation classes. */
    getStatusClassName?: (status: TransitionStatus) => string
    /** Enter/exit duration in ms. Defaults to 300. */
    transitionTimeout?: number
    /** Fired once the exit animation has finished. */
    onExited?: () => void
}

/**
 * Dialog.AnimatedRoot - native `<dialog>` building block with a scale + fade enter/exit
 *
 * **Role in Compound Structure:**
 * The animated sibling of `Dialog.Root`. Same controlled `open` + `onClose` contract, but mounts
 * through `react-transition-state` so the card scales + fades and the backdrop fades on enter/exit.
 * Prefer `Dialog.Root` for high-traffic pages where the extra mount/transition cost isn't worth it.
 *
 * Purely presentational — all behaviour (transition lifecycle, Escape and backdrop handling) lives
 * in `useAnimatedDialog`. The per-status classes map the transition `status` to the scale + fade
 * (override via `getStatusClassName`). Forwards any extra native `<dialog>` attributes onto the
 * element.
 */
const AnimatedDialog = ({
    open,
    onClose,
    closeOnBackdropClick = false,
    className,
    children,
    getStatusClassName = getDialogStatusClassName,
    transitionTimeout = 300,
    onExited,
    ...rest
}: AnimatedDialogProps) => {
    const { status, isMounted, dialogRef, onBackdropClick } = useAnimatedDialog({
        open,
        onClose,
        closeOnBackdropClick,
        transitionTimeout,
        onExited,
    })

    if (!isMounted) {
        return null
    }

    return (
        <dialog
            ref={dialogRef}
            {...rest}
            onClick={onBackdropClick}
            className={twMerge(
                DIALOG_BASE,
                'origin-center backdrop:bg-[#10185a]',
                getStatusClassName(status),
                className,
            )}>
            {children}
        </dialog>
    )
}

export default AnimatedDialog
